<template>
  <div>
    <div class="flex flex-col md:flex-row">
      <!-- left section -->
      <div class="w-screen justify-center md:content-center md:h-screen md:w-1/2 sm:content-start bg-white flex flex-wrap">
        <div class="mx-auto w-full max-w-screen-sm md:w-full sm:w-3/4 lg:p-14 md:p-8 p-6 bg-white">
          <!-- <img v-if="companyLogo" :src="companyLogo" class="mb-10 w-36 mx-auto md:mx-0 mt-4 md:mt-0" /> -->
          <BrandImage :src="companyLogo" alignment="left" v-if="companyLogo" class="mb-4 mt-4 md:mt-0" />
          <h1 v-else-if="companyName" class="text-xl text-center md:text-left text-brand-black font-semibold mb-6">{{ companyName }}</h1>
          <i18n :path="promptKey" tag="p" class="text-base md:text-xl text-brand-medium-brown leading-relaxed md:mb-8 text-center md:text-left">
            <template #company><a :href="redirectLink" target="_blank" class="cursor-pointer underline underline-offset-8">{{ companyName }}</a></template>
          </i18n>
          <div class="space-y-2 divide-y divide-brand-sand">
            <p class="text-sm text-brand-black mb-2 font-medium text-center md:text-left md:block hidden">
              {{ $t('invoice.planOf', { name: customerName, plan: customerPlan }) }}
            </p>
            <!-- Desktop Invoice Items -->
            <div class="hidden md:block">
              <div class="flex items-center justify-between py-4" v-for="bill in invoices" :key="bill.invoice">
                <div class="flex align-items-center">
                  <div v-if="bill.displayDiscount" class="bg-red w-16 bg-orange-100 leading-10 text-center align-middle rounded-md text-gray-900 mr-2">
                    <span>-{{ bill.displayDiscount.coupon | formatCouponAmount }}</span>
                  </div>
                  <div class="">
                    <span>
                      <span v-if="bill.displayDiscount" class="text-brand-medium-brown font-medium mr-1 line-through">{{
                        bill | amountBeforeDiscount | formatAmount
                      }}</span>
                      <span class="text-brand-black font-semibold">{{ bill | amountAfterDiscount | formatAmount }}</span>
                    </span>
                    <p class="text-xs text-brand-medium-brown font-medium">
                      <!-- Future invoice preview with discount -->
                      <span v-if="bill.status === 'draft' && bill.displayDiscount">
                        <!-- Forever future discount -->
                        <i18n v-if="bill.displayDiscount.coupon.couponDuration === null" path="discount.startingForever">
                          <template #date>{{ bill.periodEnd | formatDate }}</template>
                        </i18n>
                        <!-- Temporary future discount -->
                        <span v-else>
                          {{ bill.periodEnd | formatDate }}
                          {{ bill.displayDiscount.coupon | translateCouponDuration('times') }}
                        </span>
                      </span>
                      <span v-else>
                        <!-- Regular date display -->
                        {{ bill.periodEnd | formatDate }}
                      </span>
                    </p>
                  </div>
                </div>
                <span v-if="bill.status === 'paid'" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.paid') }}</span>
                <span v-else-if="bill.status === 'uncollectible'" class="bg-red-100 rounded-3xl font-semibold text-sm px-4 py-1 text-red-900">{{ $t('status.pastDue') }}</span>
                <span v-else-if="bill.status === 'draft' && !bill.invoice" class="bg-fuchsia-200 rounded-3xl font-semibold text-sm px-4 py-1 text-fuchsia-900"
                  >{{ $t('status.futureOffer') }}</span
                >
                <span v-else class="bg-brand-sand rounded-3xl font-semibold text-sm px-4 py-1 text-brand-brown">{{ $t('status.failed') }}</span>
              </div>
            </div>

          </div>
        </div>
      </div>
      <!-- right section -->
      <div class="md:w-1/2 bg-brand-light-sand bg-neutral-800 justify-center md:content-center xs:content-start flex flex-wrap pt-6 md:pt-0">
        <StripeDemoForm v-if="account" class="px-0 pt-0 bg-brand-light-sand" />

      <!-- Mobile Accordion Toggle -->
      <div class="block md:hidden mx-auto w-full max-w-screen-sm sm:w-3/4 p-6">
        <div class="flex items-center justify-between pb-4 cursor-pointer" @click="toggleInvoiceDetails">
          <span class="text-sm font-medium text-brand-black">{{ $t('invoice.seeInvoiceHistory') }}</span>
          <svg
            class="w-5 h-5 transform transition-transform duration-200"
            :class="{ 'rotate-180': showInvoiceDetails }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <!-- Mobile Invoice Items (Accordion) -->
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="transform opacity-0 ease-out"
          enter-to-class="transform opacity-100 ease-out"
          leave-active-class="transition duration-200 ease-out"
          leave-from-class="transform opacity-100 ease-out"
          leave-to-class="transform opacity-0 ease-out"
        >
          <div v-show="showInvoiceDetails" class="block md:hidden">
            <div class="flex items-center justify-between py-4" v-for="bill in invoices" :key="bill.invoice">
              <div class="flex align-items-center">
                <div v-if="bill.displayDiscount" class="bg-red w-16 bg-orange-100 leading-10 text-center align-middle rounded-md text-gray-900 mr-2">
                  <span>-{{ bill.displayDiscount.coupon | formatCouponAmount }}</span>
                </div>
                <div class="">
                  <span>
                    <span v-if="bill.displayDiscount" class="text-brand-medium-brown font-medium mr-1 line-through">{{
                      bill | amountBeforeDiscount | formatAmount
                    }}</span>
                    <span class="text-brand-black font-semibold">{{ bill | amountAfterDiscount | formatAmount }}</span>
                  </span>
                  <p class="text-xs text-brand-medium-brown font-medium">
                    <!-- Future invoice preview with discount -->
                    <span v-if="bill.status === 'draft' && bill.displayDiscount">
                      <!-- Forever future discount -->
                      <i18n v-if="bill.displayDiscount.coupon.couponDuration === null" path="discount.startingForever">
                        <template #date>{{ bill.periodEnd | formatDate }}</template>
                      </i18n>
                      <!-- Temporary future discount -->
                      <span v-else>
                        {{ bill.periodEnd | formatDate }}
                        {{ bill.displayDiscount.coupon | translateCouponDuration('times') }}
                      </span>
                    </span>
                    <span v-else>
                      <!-- Regular date display -->
                      {{ bill.periodEnd | formatDate }}
                    </span>
                  </p>
                </div>
              </div>
              <span v-if="bill.status === 'paid'" class="bg-green-100 rounded-3xl font-semibold text-sm px-4 py-1 text-green-900">{{ $t('status.paid') }}</span>
              <span v-else-if="bill.status === 'uncollectible'" class="bg-red-100 rounded-3xl font-semibold text-sm px-4 py-1 text-red-900">{{ $t('status.pastDue') }}</span>
              <span v-else-if="bill.status === 'draft' && !bill.invoice" class="bg-fuchsia-200 rounded-3xl font-semibold text-sm px-4 py-1 text-fuchsia-900"
                >{{ $t('status.futureOffer') }}</span
              >
              <span v-else class="bg-brand-sand rounded-3xl font-semibold text-sm px-4 py-1 text-brand-brown">{{ $t('status.failed') }}</span>
            </div>
          </div>
        </transition>
      </div>

      </div>

    </div>
  </div>
</template>

<script>
import * as Filters from '@/helpers/filters';
import { getIntegerCurrencyString } from '@/helpers/currency-utils';
import StripeDemoForm from '@/components/StripeDemoForm.vue';
import EventBus from '@/helpers/event-bus';
import BrandImage from '@/components/BrandImage.vue';

const monthDaysAgo = (months, days) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  date.setDate(date.getDate() - days);
  return date;
};

const demoInvoiceDue = {
  status: 'open',
  periodEnd: monthDaysAgo(0, 5),
  amountPaid: 0,
  amountDue: 7900,
  amountRemaining: 7900,
};

const demoInvoices = [
  demoInvoiceDue,
  {
    status: 'paid',
    periodEnd: monthDaysAgo(1, 5),
    amountPaid: 7900,
    amountDue: 0,
    amountRemaining: 0,
  },
  {
    status: 'paid',
    periodEnd: monthDaysAgo(2, 5),
    amountPaid: 7900,
    amountDue: 0,
    amountRemaining: 0,
  },
];

export default {
  name: 'demo',
  components: {
    StripeDemoForm,
    BrandImage,
  },
  async created() {
    await this.$store.dispatch('FETCH_CONTEXT');
  },
  data() {
    return {
      invoices: demoInvoices,
      customerPlan: 'Startup Growth Plan',
      customerName: 'Elizabeth',
      showInvoiceDetails: false
    };
  },
  mounted() {
    EventBus.$on('invoicePaid', (invoice) => this.markInvoicePaid(invoice));
  },
  methods: {
    markInvoicePaid(invoice) {
      this.invoices = this.invoices.map((i) => {
        if (i.status !== 'paid') {
          i.status = 'paid';
          i.amountPaid = i.amountDue;
          i.amountRemaining = 0;
        }
        return i;
      });
      const invoiceIndex = this.invoices.findIndex((i) => i.invoice === invoice.invoice);
      if (invoiceIndex !== -1) {
        this.invoices[invoiceIndex] = invoice;
      }
    },
    toggleInvoiceDetails() {
      this.showInvoiceDetails = !this.showInvoiceDetails;
    }
  },
  computed: {
    account() {
      return this.$store.state?.account;
    },
    companyName() {
      return this.account?.name;
    },
    companyLogo() {
      return this.account?.branding?.brandImage;
    },
    promptKey() {
      return this.$store.getters.isNonProfit ? 'prompt.updateNonProfit' : 'prompt.update';
    },
    redirectLink() {
      let link = this.account?.redirectLink;
      // prepend https:// if not already there
      if (link && link.indexOf('://') === -1) {
        link = `https://${link}`;
      }
      return link;
    },
  },
  filters: {
    formatDate: Filters.formatDate,
    formatAmount(amount) {
      return getIntegerCurrencyString(amount, 'usd', false);
    },
    amountBeforeDiscount(invoice) {
      if (invoice.displayDiscount !== undefined) {
        return invoice.displayDiscount.amountBeforeDiscount;
      }
      return '';
    },
    amountAfterDiscount(invoice) {
      if (invoice.displayDiscount !== undefined) {
        return invoice.displayDiscount.amountAfterDiscount;
      }
      if (invoice.status === 'paid') return invoice.amountPaid;
      return invoice.amountRemaining;
    },
    translateCouponDuration: Filters.translateCouponDuration,
    formatCouponAmount: Filters.formatCouponAmount,
  },
};
</script>

<style lang="scss">
@import '@/assets/style.scss';
</style>
